(() => {
  const roots = document.querySelectorAll('[data-manual-finder]');

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const normalize = (value = '') => value
    .normalize('NFKD')
    .replace(/ⅱ/gi, 'ii')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\u3400-\u9fff]+/g, ' ')
    .trim();

  const initials = (name) => name
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  const pluralize = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

  const pushEvent = (name, details = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...details });
  };

  const updateUrl = (state, mode = 'replace') => {
    const url = new URL(window.location.href);
    const values = {
      product: state.selectedProduct,
      lang: state.selectedLanguage === 'all' ? '' : state.selectedLanguage,
      category: state.category === 'all' ? '' : state.category,
      q: state.query,
    };

    Object.entries(values).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    });

    window.history[mode === 'push' ? 'pushState' : 'replaceState']({}, '', url);
  };

  const getLocaleLanguage = (locale) => {
    const value = String(locale || '').toLowerCase();
    if (value.startsWith('zh-tw') || value.startsWith('zh-hk')) return 'zh-TW';
    if (value.startsWith('zh')) return 'zh-CN';
    return value.split('-')[0] || 'en';
  };

  const chooseLanguage = (product, requestedLanguage, locale) => {
    const codes = product.manuals.map((manual) => manual.languageCode);
    if (requestedLanguage && codes.includes(requestedLanguage)) return requestedLanguage;
    const localeLanguage = getLocaleLanguage(locale);
    const exact = codes.find((code) => code.toLowerCase() === localeLanguage.toLowerCase());
    if (exact) return exact;
    const base = localeLanguage.split('-')[0];
    const related = codes.find((code) => code.toLowerCase().split('-')[0] === base);
    if (related) return related;
    if (codes.includes('en')) return 'en';
    return codes.length === 1 ? codes[0] : 'all';
  };

  const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '';
    const megabytes = bytes / (1024 * 1024);
    if (megabytes >= 1) return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(megabytes)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
  };

  const sizedImageUrl = (src, width) => {
    try {
      const url = new URL(src);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return src;
    }
  };

  const makeMedia = (product, className, imageWidth = 480, imageOptions = {}) => {
    const media = createElement('div', className);
    if (product.image?.src) {
      const image = document.createElement('img');
      image.src = sizedImageUrl(product.image.src, imageWidth);
      if (imageOptions.widths?.length) {
        image.srcset = imageOptions.widths
          .map((width) => `${sizedImageUrl(product.image.src, width)} ${width}w`)
          .join(', ');
      }
      if (imageOptions.sizes) image.sizes = imageOptions.sizes;
      image.alt = product.image.alt || `${product.name} product`;
      image.width = 480;
      image.height = 480;
      image.loading = imageOptions.loading || 'lazy';
      image.fetchPriority = imageOptions.fetchPriority || 'auto';
      image.decoding = 'async';
      media.append(image);
    } else {
      media.append(createElement('span', 'pmn__product-placeholder', initials(product.name)));
    }
    return media;
  };

  const mount = async (root) => {
    const elements = {
      search: root.querySelector('[data-manual-search]'),
      clear: root.querySelector('[data-manual-clear]'),
      suggestions: root.querySelector('[data-manual-suggestions]'),
      categories: root.querySelector('[data-manual-categories]'),
      products: root.querySelector('[data-manual-products]'),
      loading: root.querySelector('[data-manual-loading]'),
      empty: root.querySelector('[data-manual-empty]'),
      error: root.querySelector('[data-manual-error]'),
      reset: root.querySelector('[data-manual-reset]'),
      resultCount: root.querySelector('[data-manual-result-count]'),
      productTotal: root.querySelector('[data-manual-product-total]'),
      fileTotal: root.querySelector('[data-manual-file-total]'),
      detail: root.querySelector('[data-manual-detail]'),
      detailTitle: root.querySelector('[data-manual-detail-title]'),
      detailAliases: root.querySelector('[data-manual-detail-aliases]'),
      detailMedia: root.querySelector('[data-manual-detail-media]'),
      language: root.querySelector('[data-manual-language]'),
      manualList: root.querySelector('[data-manual-list]'),
      tutorial: root.querySelector('[data-manual-tutorial]'),
      tutorialTitle: root.querySelector('[data-manual-tutorial-title]'),
      tutorialVideo: root.querySelector('[data-manual-video]'),
    };

    const params = new URL(window.location.href).searchParams;
    const state = {
      data: null,
      query: params.get('q') || '',
      category: params.get('category') || 'all',
      selectedProduct: params.get('product') || '',
      selectedLanguage: params.get('lang') || '',
      activeSuggestion: -1,
    };

    const searchText = (product) => normalize([
      product.name,
      product.key,
      product.sourceName,
      ...(product.aliases || []),
    ].join(' '));

    const getFilteredProducts = () => {
      const query = normalize(state.query);
      return state.data.products.filter((product) => {
        const matchesCategory = state.category === 'all' || product.category === state.category;
        const matchesQuery = !query || searchText(product).includes(query);
        return matchesCategory && matchesQuery;
      });
    };

    const closeSuggestions = () => {
      state.activeSuggestion = -1;
      elements.suggestions.hidden = true;
      elements.suggestions.replaceChildren();
      elements.search.setAttribute('aria-expanded', 'false');
      elements.search.removeAttribute('aria-activedescendant');
    };

    const renderTutorial = (product) => {
      elements.tutorialVideo.replaceChildren();

      // Backward compatible:
      // - Existing products can keep: tutorial: { youtubeId, start }
      // - Products with language-specific videos can use:
      //   tutorials: [{ language, languageCode, youtubeId, start }, ...]
      const tutorials = Array.isArray(product.tutorials) && product.tutorials.length
        ? product.tutorials.filter((item) => item?.youtubeId)
        : (product.tutorial?.youtubeId ? [{ ...product.tutorial }] : []);

      if (!tutorials.length) {
        elements.tutorial.hidden = true;
        return;
      }

      const selectedLanguage = state.selectedLanguage || 'all';
      let tutorial;

      if (selectedLanguage !== 'all') {
        tutorial = tutorials.find((item) => item.languageCode === selectedLanguage)
          || tutorials.find((item) => item.languageCode === 'multi')
          || tutorials.find((item) => item.languageCode === 'en')
          || tutorials[0];
      } else {
        tutorial = tutorials.find((item) => item.languageCode === 'en')
          || tutorials.find((item) => item.languageCode === 'multi')
          || tutorials[0];
      }

      const {
        youtubeId,
        start = 0,
        language = '',
        languageCode = '',
      } = tutorial;

      const languageSuffix = language ? ` · ${language}` : '';
      const tutorialLabel = `${product.name} video tutorial${languageSuffix}`;
      elements.tutorialTitle.textContent = tutorialLabel;

      const launch = createElement('button', 'pmn__video-launch');
      launch.type = 'button';
      launch.setAttribute('aria-label', `Play ${tutorialLabel}`);

      const poster = document.createElement('img');
      poster.src = `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
      poster.alt = `${tutorialLabel} preview`;
      poster.width = 480;
      poster.height = 360;
      poster.loading = 'lazy';
      poster.decoding = 'async';

      const play = createElement('span', 'pmn__video-play');
      play.setAttribute('aria-hidden', 'true');
      launch.append(poster, play);
      launch.addEventListener('click', () => {
        const iframe = document.createElement('iframe');
        const startParam = start ? `&start=${start}` : '';
        iframe.src = `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&controls=1&playsinline=1&rel=0${startParam}`;
        iframe.title = tutorialLabel;
        iframe.loading = 'lazy';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.setAttribute('allowfullscreen', '');
        elements.tutorialVideo.replaceChildren(iframe);
        pushEvent('manual_tutorial_play', {
          manual_product: product.key,
          tutorial_video_id: youtubeId,
          tutorial_language: languageCode,
        });
      }, { once: true });

      elements.tutorialVideo.append(launch);
      elements.tutorial.hidden = false;
    };

    const selectProduct = (product, options = {}) => {
      const { scroll = true, history = 'push', focus = true } = options;
      state.selectedProduct = product.slug;
      state.selectedLanguage = chooseLanguage(product, state.selectedLanguage, root.dataset.locale);
      closeSuggestions();

      const detailMediaContent = makeMedia(product, '', 480, {
        widths: [360, 480, 720],
        sizes: '(max-width: 47.99rem) calc(100vw - 82px), 420px',
        loading: 'eager',
        fetchPriority: 'high',
      });
      elements.detailMedia.replaceChildren(...detailMediaContent.childNodes);
      elements.detailTitle.textContent = `${product.name} manuals`;

      const seenAliases = new Set([normalize(product.name)]);
      const readableAliases = (product.aliases || []).filter((alias) => {
        const normalizedAlias = normalize(alias);
        if (!normalizedAlias || seenAliases.has(normalizedAlias) || /[\u3400-\u9fff]/.test(alias)) return false;
        seenAliases.add(normalizedAlias);
        return true;
      }).slice(0, 3);
      elements.detailAliases.hidden = readableAliases.length === 0;
      elements.detailAliases.textContent = readableAliases.length ? `Also known as: ${readableAliases.join(' · ')}` : '';

      const languageOptions = [{ code: 'all', label: `All languages (${product.manuals.length})` }];
      product.manuals.forEach((manual) => {
        if (!languageOptions.some((option) => option.code === manual.languageCode)) {
          languageOptions.push({ code: manual.languageCode, label: manual.language });
        }
      });
      elements.language.replaceChildren(...languageOptions.map((option) => {
        const element = createElement('option', '', option.label);
        element.value = option.code;
        element.selected = option.code === state.selectedLanguage;
        if (option.code !== 'all') element.setAttribute('translate', 'no');
        return element;
      }));

      renderManuals(product);
      renderTutorial(product);
      elements.detail.hidden = false;
      elements.products.querySelectorAll('.pmn__product-card').forEach((card) => {
        const selected = card.dataset.product === product.slug;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-expanded', String(selected));
      });
      updateUrl(state, history);
      pushEvent('manual_product_select', { manual_product: product.key });

      if (scroll) {
        window.requestAnimationFrame(() => {
          elements.detail.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
          if (focus) window.setTimeout(() => elements.detailTitle.focus({ preventScroll: true }), 250);
        });
      }
    };

    const renderManuals = (product) => {
      const manuals = state.selectedLanguage === 'all'
        ? product.manuals
        : product.manuals.filter((manual) => manual.languageCode === state.selectedLanguage);

      const rows = manuals.map((manual) => {
        const row = createElement('article', 'pmn__manual-row');
        const identity = createElement('div', 'pmn__manual-identity');
        const language = createElement('p', 'pmn__manual-language', manual.language);
        language.setAttribute('translate', 'no');
        identity.append(
          language,
          createElement('p', 'pmn__manual-filename', manual.filename),
        );
        const metaParts = [];
        if (manual.pages) metaParts.push(pluralize(manual.pages, 'page'));
        if (manual.sizeBytes) metaParts.push(formatBytes(manual.sizeBytes));
        const meta = createElement('span', 'pmn__manual-meta', metaParts.join(' · '));
        const link = createElement('a', 'pmn__manual-link', 'Open PDF');
        link.href = manual.url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.setAttribute('aria-label', `Open ${product.name} manual in ${manual.language} as PDF`);
        link.addEventListener('click', () => pushEvent('manual_open', {
          manual_product: product.key,
          manual_language: manual.languageCode,
          manual_url: manual.url,
        }));
        row.append(identity, meta, link);
        return row;
      });
      elements.manualList.replaceChildren(...rows);
    };

    const renderSuggestions = () => {
      const query = normalize(state.query);
      if (!query) {
        closeSuggestions();
        return;
      }

      const matches = state.data.products
        .filter((product) => searchText(product).includes(query))
        .slice(0, 6);
      if (!matches.length) {
        closeSuggestions();
        return;
      }

      const options = matches.map((product, index) => {
        const button = createElement('button', 'pmn__suggestion');
        button.type = 'button';
        button.id = `${root.id}-suggestion-${index}`;
        button.setAttribute('role', 'option');
        button.dataset.suggestionIndex = String(index);
        const media = makeMedia(product, 'pmn__suggestion-thumb', 96);
        const name = createElement('span', 'pmn__suggestion-name', product.name);
        const meta = createElement('span', 'pmn__suggestion-meta', pluralize(product.manuals.length, 'manual'));
        button.append(media, name, meta);
        button.addEventListener('mousedown', (event) => event.preventDefault());
        button.addEventListener('click', (event) => selectProduct(product, { focus: event.detail === 0 }));
        return button;
      });
      state.activeSuggestion = -1;
      elements.suggestions.replaceChildren(...options);
      elements.suggestions.hidden = false;
      elements.search.setAttribute('aria-expanded', 'true');
    };

    const renderProducts = () => {
      const products = getFilteredProducts();
      const cards = products.map((product, index) => {
        const card = createElement('button', 'pmn__product-card');
        card.type = 'button';
        card.dataset.product = product.slug;
        card.setAttribute('aria-controls', elements.detail.id);
        card.setAttribute('aria-expanded', String(state.selectedProduct === product.slug));

        const media = makeMedia(product, 'pmn__product-media', 360, {
          widths: [240, 360, 480],
          sizes: '(max-width: 31.24rem) 120px, (max-width: 47.99rem) calc(50vw - 36px), (max-width: 61.99rem) calc(33vw - 32px), 300px',
          fetchPriority: index < 4 && !state.selectedProduct ? 'auto' : 'low',
        });
        const body = createElement('span', 'pmn__product-body');
        body.append(
          createElement('span', 'pmn__product-category', product.categoryLabel),
          createElement('span', 'pmn__product-name', product.name),
        );
        const foot = createElement('span', 'pmn__product-foot');
        foot.append(
          createElement('span', 'pmn__product-count', pluralize(product.manuals.length, 'manual')),
          createElement('span', 'pmn__product-action', 'View manuals'),
        );
        body.append(foot);
        card.append(media, body);
        card.addEventListener('click', (event) => selectProduct(product, { focus: event.detail === 0 }));
        return card;
      });

      elements.products.replaceChildren(...cards);
      elements.products.hidden = products.length === 0;
      elements.empty.hidden = products.length !== 0;
      elements.resultCount.textContent = products.length
        ? pluralize(products.length, 'product')
        : 'No matching products';
      elements.clear.hidden = !state.query;

      if (state.selectedProduct && !products.some((product) => product.slug === state.selectedProduct)) {
        state.selectedProduct = '';
        state.selectedLanguage = '';
        elements.detail.hidden = true;
      }
    };

    const renderCategories = () => {
      const makeCategoryButton = (key, label, count, active = false) => {
        const button = createElement('button', `pmn__category${active ? ' is-active' : ''}`);
        button.type = 'button';
        button.dataset.category = key;
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', `${label}, ${pluralize(count, 'product')}`);
        button.append(
          createElement('span', 'pmn__category-label', label),
          createElement('span', 'pmn__category-count', String(count))
        );
        return button;
      };

      const buttons = [makeCategoryButton('all', 'All products', state.data.products.length, true)];
      state.data.categories.forEach((category) => {
        const count = state.data.products.filter((product) => product.category === category.key).length;
        buttons.push(makeCategoryButton(category.key, category.label, count));
      });
      elements.categories.replaceChildren(...buttons);

      elements.categories.addEventListener('click', (event) => {
        const button = event.target.closest('[data-category]');
        if (!button) return;
        state.category = button.dataset.category;
        elements.categories.querySelectorAll('[data-category]').forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        renderProducts();
        updateUrl(state);
        pushEvent('manual_category_select', { manual_category: state.category });
      });
    };

    const restoreFromUrl = () => {
      const current = new URL(window.location.href).searchParams;
      state.query = current.get('q') || '';
      state.category = current.get('category') || 'all';
      state.selectedProduct = current.get('product') || '';
      state.selectedLanguage = current.get('lang') || '';
      elements.search.value = state.query;
      elements.categories.querySelectorAll('[data-category]').forEach((button) => {
        const active = button.dataset.category === state.category;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      renderProducts();
      const product = state.data.products.find((item) => item.slug === state.selectedProduct);
      if (product) selectProduct(product, { scroll: false, history: 'replace' });
      else elements.detail.hidden = true;
      updateUrl(state, 'replace');
    };

    elements.search.addEventListener('input', () => {
      state.query = elements.search.value;
      renderProducts();
      renderSuggestions();
      updateUrl(state);
      pushEvent('manual_search', { manual_search_term: state.query, manual_result_count: getFilteredProducts().length });
      if (state.query && getFilteredProducts().length === 0) {
        pushEvent('manual_no_results', { manual_search_term: state.query });
      }
    });

    elements.search.addEventListener('keydown', (event) => {
      const options = [...elements.suggestions.querySelectorAll('[role="option"]')];
      if (!options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        state.activeSuggestion = (state.activeSuggestion + direction + options.length) % options.length;
        options.forEach((option, index) => option.classList.toggle('is-active', index === state.activeSuggestion));
        elements.search.setAttribute('aria-activedescendant', options[state.activeSuggestion].id);
      } else if (event.key === 'Enter' && state.activeSuggestion >= 0) {
        event.preventDefault();
        options[state.activeSuggestion].click();
      } else if (event.key === 'Escape') {
        closeSuggestions();
      }
    });

    elements.search.addEventListener('blur', () => window.setTimeout(closeSuggestions, 100));
    elements.search.addEventListener('focus', renderSuggestions);

    elements.clear.addEventListener('click', () => {
      state.query = '';
      elements.search.value = '';
      closeSuggestions();
      renderProducts();
      updateUrl(state);
      elements.search.focus();
    });

    elements.reset.addEventListener('click', () => {
      state.query = '';
      state.category = 'all';
      elements.search.value = '';
      elements.categories.querySelectorAll('[data-category]').forEach((button) => {
        const active = button.dataset.category === 'all';
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      renderProducts();
      updateUrl(state);
      elements.search.focus();
    });

    elements.language.addEventListener('change', () => {
      state.selectedLanguage = elements.language.value;
      const product = state.data.products.find((item) => item.slug === state.selectedProduct);
      if (!product) return;
      renderManuals(product);
      renderTutorial(product);
      updateUrl(state);
      pushEvent('manual_language_select', {
        manual_product: product.key,
        manual_language: state.selectedLanguage,
      });
    });

    window.addEventListener('popstate', restoreFromUrl);

    try {
      const response = await fetch(root.dataset.manualsUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Manual data request failed with ${response.status}`);
      state.data = await response.json();
      if (!Array.isArray(state.data.products) || !state.data.products.length) throw new Error('Manual data contains no products');

      elements.productTotal.textContent = state.data.totals.products;
      elements.fileTotal.textContent = state.data.totals.manuals;
      elements.search.value = state.query;
      renderCategories();
      elements.loading.hidden = true;
      elements.products.hidden = false;
      restoreFromUrl();
    } catch (error) {
      console.error('[Manual Finder]', error);
      elements.loading.hidden = true;
      elements.error.hidden = false;
      elements.resultCount.textContent = 'Manual library unavailable';
    }
  };

  roots.forEach(mount);
})();
